import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon } from '@/components/icons';
import { Input, LoadingSpinner } from '@/components/ui';
import {
  NOMINALS_TAB_ICON,
  USERS_TAB_ICON,
} from '@/features/addressBook/addressBookShared';
import {
  useOnlineContactsPanel,
  type OnlineContactsView,
} from '@/features/onlineContacts/useOnlineContactsPanel';
import { presenceForUserId } from '@/features/onlineContacts/presenceForUserId';
import {
  managementNameLabel,
  personDisplayName,
} from '@/features/userManagement/userManagementUtils';
import {
  CARD_SECTION_CLASS,
  ONLINE_CONTACTS_PANEL_HEIGHT,
} from '@/styles/classes';
import type { User } from '@/types/api';
import type { Nominal } from '@/types/nominals';
import type { PresenceRow } from '@/types/presence';

type OnlineContactsPanelProps = {
  user: User;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

const AVATAR_TONES = [
  'bg-blue-navbar text-white',
  'bg-status-active-text text-white',
  'bg-unilink-text-secondary text-white',
];

function avatarTone(id: string): string {
  const total = [...id].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_TONES[total % AVATAR_TONES.length];
}

function initialsFor(label: string): string {
  const words = label
    .replace(/\(.*\)/, '')
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean);
  const letters = words.slice(0, 2).map(word => word[0].toUpperCase());
  return letters.join('') || label.slice(0, 2).toUpperCase();
}

type ContactRowProps = {
  href: string;
  userId: string;
  label: string;
  detail: string;
  presence: PresenceRow | undefined;
  inCallLabel: string;
};

function ContactRow({
  href,
  userId,
  label,
  detail,
  presence,
  inCallLabel,
}: ContactRowProps) {
  const isOnline = presence?.is_online === true;
  return (
    <Link
      to={href}
      className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 hover:bg-unilink-surface-hover"
    >
      <span className="relative shrink-0">
        <span
          className={clsx(
            'flex size-8 items-center justify-center rounded-full text-[11px] font-bold',
            avatarTone(userId)
          )}
          aria-hidden
        >
          {initialsFor(label)}
        </span>
        <span
          className={clsx(
            'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-white',
            isOnline ? 'bg-status-active-text' : 'bg-unilink-border-outline'
          )}
          aria-hidden
        />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={clsx(
            'block truncate text-xs font-semibold leading-4',
            isOnline ? 'text-unilink-text-primary' : 'text-unilink-text-secondary'
          )}
        >
          {label}
        </span>
        <span className="block truncate text-[11px] leading-4 text-unilink-text-secondary">
          {presence?.is_in_call ? inCallLabel : detail}
        </span>
      </span>
    </Link>
  );
}

export function OnlineContactsPanel({
  user,
  expanded: expandedProp,
  onExpandedChange,
}: OnlineContactsPanelProps) {
  const { t } = useTranslation();
  const [expandedState, setExpandedState] = useState(true);
  const expanded = expandedProp ?? expandedState;
  const panel = useOnlineContactsPanel({ user, t });

  const toggleExpanded = () => {
    const next = !expanded;
    if (expandedProp === undefined) {
      setExpandedState(next);
    }
    onExpandedChange?.(next);
  };

  const viewTabs: { id: OnlineContactsView; labelKey: string }[] = [
    { id: 'users', labelKey: 'common.users' },
    { id: 'nominals', labelKey: 'common.nominals' },
  ];

  const filteredList =
    panel.activeView === 'users' ? panel.filteredUsers : panel.filteredNominals;

  const listItems =
    panel.activeView === 'users'
      ? panel.filteredUsers.map((row: User) => (
          <ContactRow
            key={row.id}
            href={`/users/${row.id}`}
            userId={row.id}
            label={managementNameLabel(row)}
            detail={row.freeswitch_extension || t('common.na')}
            presence={presenceForUserId(panel.presenceByUserId, row.id)}
            inCallLabel={t('presence.inCall')}
          />
        ))
      : panel.filteredNominals.map((row: Nominal) => (
          <ContactRow
            key={row.id}
            href={`/nominals/${row.id}`}
            userId={row.id}
            label={personDisplayName(row, row.username)}
            detail={row.prisoner_number || row.username}
            presence={presenceForUserId(panel.presenceByUserId, row.id)}
            inCallLabel={t('presence.inCall')}
          />
        ));

  const activeListCount =
    panel.activeView === 'users' ? panel.usersCount : panel.nominalsCount;
  const searchTrimmed = panel.searchQuery.trim();
  const activeListLabelKey =
    panel.activeView === 'users' ? 'common.users' : 'common.nominals';
  const collapsedTitle = t('addressBook.tabWithCount', {
    label: t(activeListLabelKey),
    count: activeListCount,
  });

  return (
    <div
      className={clsx(
        'flex min-h-0 flex-col',
        expanded ? 'w-full' : 'relative ml-auto w-max max-w-[50vw]',
        expanded && ONLINE_CONTACTS_PANEL_HEIGHT
      )}
      aria-label={t('onlineContacts.panelLabel')}
    >
      <button
        type="button"
        className={clsx(
          'flex shrink-0 items-center gap-2 rounded bg-blue-navbar px-3 py-2.5 text-left text-white shadow-md hover:bg-blue-navbar/90',
          expanded ? 'w-full' : 'w-max'
        )}
        aria-expanded={expanded}
        onClick={toggleExpanded}
      >
        <Icon
          icon={panel.activeView === 'users' ? USERS_TAB_ICON : NOMINALS_TAB_ICON}
          className="size-4 shrink-0 text-white"
        />
        <span className="min-w-0 flex-1 text-sm font-semibold leading-tight">
          {collapsedTitle}
        </span>
        <span
          className={clsx(
            'size-2 shrink-0 rounded-full',
            panel.isConnected ? 'bg-status-active-bg' : 'bg-status-listening-bg'
          )}
          aria-hidden
        />
        <Icon
          icon="chevron-down"
          className={clsx(
            'size-4 shrink-0 text-white transition-transform',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded ? (
        <section
          className={clsx(
            CARD_SECTION_CLASS,
            'mt-2 flex min-h-0 w-full flex-1 flex-col overflow-hidden border-t-2 border-blue-navbar'
          )}
        >
          <div className="mx-2 mt-2 flex shrink-0 rounded-lg bg-unilink-surface-panel p-0.5">
            {viewTabs.map(tab => {
              const selected = panel.activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => panel.setActiveView(tab.id)}
                  className={clsx(
                    'flex min-w-0 flex-1 items-center justify-center rounded-md px-1 py-1.5 leading-tight',
                    selected
                      ? 'bg-white text-blue-navbar shadow-sm'
                      : 'text-unilink-text-secondary hover:text-unilink-text-primary'
                  )}
                >
                  <span className="text-[11px] font-semibold">{t(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>

          <div className="shrink-0 px-2 pb-2 pt-1">
            <Input
              type="search"
              placeholder={t('onlineContacts.searchPlaceholder')}
              value={panel.searchQuery}
              onChange={event => panel.setSearchQuery(event.target.value)}
              aria-label={t('onlineContacts.searchPlaceholder')}
              className="py-1.5 text-xs"
            />
          </div>

          <div className="flex shrink-0 items-center gap-1.5 px-3 pb-2">
            <span
              className={clsx(
                'size-2 shrink-0 rounded-full',
                panel.isConnected ? 'bg-status-active-text' : 'bg-status-listening-text'
              )}
              aria-hidden
            />
            <p className="min-w-0 text-[11px] leading-4 text-unilink-text-secondary">
              {t('onlineContacts.onlineNowCount', { count: panel.onlineCount })}
            </p>
          </div>

          {panel.error ? (
            <p className="shrink-0 px-3 pb-2 text-sm text-unilink-danger-strong">
              {panel.error}
            </p>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
            {panel.loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : filteredList.length ? (
              listItems
            ) : (
              <p className="px-2 py-4 text-sm text-unilink-text-secondary">
                {searchTrimmed
                  ? t('onlineContacts.noSearchResults')
                  : panel.activeView === 'users'
                    ? t('onlineContacts.noOnlineUsers')
                    : t('onlineContacts.noOnlineNominals')}
              </p>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
